import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plus, Minus, ArrowRight, ArrowUpRight } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import StorefrontMockup from "../components/StorefrontMockup";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /shopify-experts-development — Sunday Digital
   Built on V5's design system. No new UX/UI grammar; same
   Eyebrow, mask-wipe reveal, yellow underline draw, italic-
   serif accent, slow editorial transitions. Every section is
   a remix of patterns proven on the home page.
============================================================ */

/* ----- Local atoms ----- */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

/* ============================================================
   01 · Hero — compressed editorial + tilted storefront mockup
============================================================ */
const ShopifyDevHero = ({ onPrimary }) => {
  const cms = useContent("shopify-dev");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "Shopify Development";
  const eyebrow = h.eyebrow || "eCommerce · Shopify & Shopify Plus";
  const titleLine1 = h.title_line_1 || "Built on Shopify.";
  const titleEmphasis = h.title_emphasis || "Engineered";
  const titleLine2 = h.title_line_2 || "to scale";
  const subtitle = h.subtitle ||
    "One boutique team, every layer of Shopify. From storefront craft to checkout engineering, we partner with brands ready to move beyond templates and app bloat.";
  const ctaLabel = h.cta_primary_label || h.cta_label || "Start a Project";
  const secondaryLabel = h.cta_secondary_label || "See capabilities";
  const secondaryHref = h.cta_secondary_href || "#capabilities";
  return (
    <section
      id="top"
      data-testid="dev-hero"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      {/* Editorial grid backdrop — replaces the home page particles */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(244,214,78,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(244,214,78,0.045) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(900px 600px at 70% 20%, black, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(900px 600px at 70% 20%, black, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1100px 600px at 80% 0%, rgba(244,214,78,0.08), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
        {/* Breadcrumb */}
        <div
          data-reveal="eyebrow"
          className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
        >
          <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span className="text-[#F4D64E]">{breadcrumb}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-7">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>

            <h1
              data-testid="dev-hero-title"
              className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
            >
              <span data-reveal="mask-block">
                <span>{titleLine1}</span>
              </span>
              <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
                <span>
                  <span className="italic font-normal text-neutral-400">
                    {titleEmphasis}
                  </span>{" "}
                  <PillarAccent>{titleLine2}</PillarAccent>.
                </span>
              </span>
            </h1>

            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-9 max-w-[52ch] text-[1.02rem] md:text-[1.15rem] text-neutral-300 leading-[1.6]"
            >
              <span>
                {subtitle}
              </span>
            </p>

            <div
              data-reveal="eyebrow"
              style={{ "--i": 3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryCTA testId="dev-hero-cta" onClick={onPrimary}>
                {ctaLabel}
              </PrimaryCTA>
              <a
                href={secondaryHref}
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
              >
                {secondaryLabel}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>

          {/* RIGHT — tilted storefront mockup (4 live client products) */}
          <div className="lg:col-span-5">
            <StorefrontMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   02 · Trust strip
============================================================ */
const TrustStripDev = () => {
  const cms = useContent("shopify-dev");
  const liveItems = Array.isArray(cms?.trust_strip?.items) && cms.trust_strip.items.length
    ? cms.trust_strip.items.map((it) => it.title || "").filter(Boolean)
    : ["In-house team", "250+ stores shipped", "Shopify Plus partner", "10+ years", "Brooklyn · Remote"];
  const lastIdx = liveItems.length - 1;
  return (
    <section className="relative bg-[#0B0F14] border-y border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-7">
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
          {liveItems.map((label, i) => (
            <span key={`${label}-${i}`} className="flex items-center gap-x-8">
              <span className={i === lastIdx ? "text-[#F4D64E]" : ""}>{label}</span>
              {i < lastIdx ? (
                <span aria-hidden className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
              ) : null}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   03 · "What you get" — 4-up editorial, no icons, big numerals
============================================================ */
const WHAT_YOU_GET = [
  {
    n: "01",
    title: "Free strategic audit",
    body:
      "Every engagement starts with a no-cost growth audit — what's working, what's leaking conversion, and what to fix first.",
  },
  {
    n: "02",
    title: "Direct senior contact",
    body:
      "No account managers, no offshore handoffs. You work with the senior who scopes, designs, and writes the code.",
  },
  {
    n: "03",
    title: "On-schedule delivery",
    body:
      "Timelines we commit to are timelines we hit. Fewer projects in flight, more attention on yours.",
  },
  {
    n: "04",
    title: "Custom-coded, no app bloat",
    body:
      "One-time investments instead of monthly app subscriptions. Better performance, full ownership, lower lifetime cost.",
  },
];

const WhatYouGet = () => {
  const cms = useContent("shopify-dev");
  const w = cms?.what_you_get || {};
  const eyebrow = w.eyebrow || "What you get";
  const headlineMain = w.headline_main || "Partner with us — and";
  const headlineItalic = w.headline_italic || "feel";
  const headlineAccent = w.headline_accent || "the difference";
  const description = w.description ||
    "Four commitments that shape every engagement — from a quick fix to a multi-quarter Shopify Plus build.";
  const items = Array.isArray(w.items) && w.items.length
    ? w.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
      }))
    : WHAT_YOU_GET;
  return (
  <section data-testid="what-you-get" className="relative bg-[#0B0F14]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-16 md:mb-20">
        <div className="lg:col-span-5">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65]"
          >
            <span>{description}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
        {items.map((item, i) => (
          <div
            key={`${item.n}-${i}`}
            data-reveal="mask-block"
            style={{ "--i": i }}
            data-testid={`what-you-get-${item.n}`}
          >
            <div>
              <div className="font-display text-[3.5rem] md:text-[4rem] font-black tracking-[-0.04em] leading-none text-[#F4D64E] tabular-nums">
                {item.n}
              </div>
              <h3 className="mt-5 font-display text-[1.4rem] md:text-[1.55rem] font-black tracking-[-0.015em] text-white leading-[1.15]">
                {item.title}
              </h3>
              <p className="mt-3 text-[0.95rem] text-neutral-400 leading-[1.6]">
                {item.body}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   04 · Capabilities — vertical accordion, with media reveal
============================================================ */
// Tiny media tile per capability — Pexels videos + Unsplash photos
const CAP_MEDIA = [
  { type: "video", src: "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4" }, // theme storefront
  { type: "img",   src: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1400&q=70" },
  { type: "img",   src: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=70" },
  { type: "img",   src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=70" },
  { type: "video", src: "https://videos.pexels.com/video-files/2887463/2887463-hd_1920_1080_25fps.mp4" }, // optimization
  { type: "video", src: "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4" }, // code
  { type: "img",   src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=70" },
  { type: "img",   src: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=1400&q=70" },
  { type: "video", src: "https://videos.pexels.com/video-files/854149/854149-hd_1920_1080_30fps.mp4" }, // app dev
];

const CAPABILITIES = [
  {
    n: "01",
    title: "Theme customization & UI/UX design",
    body:
      "Custom Shopify themes that look great, perform fast, and convert better. Responsive, brand-focused storefronts with motion, animation, and interactive detail where it matters.",
  },
  {
    n: "02",
    title: "Custom theme development",
    body:
      "Bespoke, responsive Shopify themes that combine smart design with advanced functionality. Built lean, easy to maintain, and ready to scale alongside the brand.",
  },
  {
    n: "03",
    title: "Shopify & Shopify Plus store setup",
    body:
      "From full setup and configuration to advanced functionality and performance optimization — we handle every detail of a launch ready to scale.",
  },
  {
    n: "04",
    title: "Theme setups & ongoing updates",
    body:
      "Initial installation, fine-tuning of layouts, styling, and features that reflect your brand — plus ongoing updates to keep the store fast, reliable, and visually consistent.",
  },
  {
    n: "05",
    title: "Store optimization & audit",
    body:
      "Deep audits that uncover performance gaps, refine design and functionality, and ensure your store runs fast, secure, and ready to convert.",
  },
  {
    n: "06",
    title: "Custom features",
    body:
      "When Shopify's defaults aren't enough, we build custom solutions — from design enhancements to advanced integrations. One-time investments, not monthly app fees.",
  },
  {
    n: "07",
    title: "Maintenance & support",
    body:
      "Once you're live, we stay close. Flexible monthly plans for fast updates, performance work, and ongoing optimization, plus tutorials so your team owns it.",
  },
  {
    n: "08",
    title: "Migration to Shopify",
    body:
      "Full migration from any platform — preserving your data, minimizing downtime, and configuring the new store for performance and growth from day one.",
  },
  {
    n: "09",
    title: "App development & white-label",
    body:
      "Custom Shopify apps that automate workflows and elevate UX. Plus white-label development for agencies — our experts working invisibly under your brand.",
  },
];

const Capabilities = () => {
  const cms = useContent("shopify-dev");
  const c = cms?.capabilities || {};
  const eyebrow = c.eyebrow || "Our capabilities";
  const headlineMain = c.headline_main || "Every layer of";
  const headlineItalic = c.headline_italic || "Shopify";
  const headlineAccent = c.headline_accent || "handled";
  const description = c.description ||
    "Nine practices, one boutique team. Senior generalists who own a discipline end-to-end — strategy, design, engineering, launch.";
  const liveItems = Array.isArray(c.items) && c.items.length
    ? c.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
        media: it.media || "",
      }))
    : CAPABILITIES.map((cap, i) => ({
        n: cap.n, title: cap.title, body: cap.body,
        media: CAP_MEDIA[i]?.src || "",
      }));
  const isVideoUrl = (u) => /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(u || "");

  const [open, setOpen] = useState(0);
  return (
    <section
      id="capabilities"
      data-testid="capabilities"
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>,{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65]"
            >
              <span>{description}</span>
            </p>
          </div>
        </div>

        <div className="border-t border-white/[0.06]">
          {liveItems.map((cap, i) => {
            const isOpen = open === i;
            const isVid = isVideoUrl(cap.media);
            return (
              <div
                key={i}
                data-reveal="eyebrow"
                style={{ "--i": Math.min(i, 5) }}
                className="border-b border-white/[0.06]"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`capability-${cap.n}`}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-6 md:gap-10 py-7 md:py-9 text-left group"
                >
                  <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 tabular-nums w-10 shrink-0">
                    {cap.n}
                  </span>
                  <span
                    className={`flex-1 font-display text-[1.6rem] md:text-[2.25rem] lg:text-[2.5rem] font-black tracking-[-0.025em] transition-colors ${
                      isOpen ? "text-[#F4D64E]" : "text-white group-hover:text-[#F4D64E]"
                    }`}
                  >
                    {cap.title}
                  </span>
                  <span
                    className={`shrink-0 inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full border transition-colors ${
                      isOpen
                        ? "border-[#F4D64E] text-[#F4D64E] bg-[#F4D64E]/10"
                        : "border-white/15 text-white group-hover:border-[#F4D64E]/60 group-hover:text-[#F4D64E]"
                    }`}
                  >
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: isOpen ? "auto" : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pb-9 md:pb-12 pl-16 md:pl-20 grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-8 items-start">
                    <p className="md:col-span-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7] max-w-[64ch]">
                      {cap.body}
                    </p>
                    {/* Media tile — fades in from right */}
                    {isOpen && cap.media && (
                      <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="md:col-span-5 relative aspect-[4/3] rounded-xl overflow-hidden border border-white/[0.08] bg-[#0F141A]"
                      >
                        {isVid ? (
                          <video
                            src={cap.media}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={cap.media}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}
                        <span
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/70 via-transparent to-transparent"
                        />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   04.5 · Build Showcase — full-bleed coding video w/ overlay
============================================================ */
const CODING_VIDEO_SRC =
  "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4";

const BuildShowcase = () => {
  const cms = useContent("shopify-dev");
  const b = cms?.build_showcase || {};
  const eyebrow = b.eyebrow || "Behind the build";
  const headlineMain = b.headline_main || "We write code.";
  const headlineItalic = b.headline_italic || "Lots";
  const headlineAccent = b.headline_accent || "of it";
  const description = b.description ||
    "Custom Liquid, React, Hydrogen, GraphQL — written by senior engineers, reviewed on every PR. No template recycling. No app stitching.";
  const video = b.video || CODING_VIDEO_SRC;
  const poster = b.poster ||
    "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=2000&q=70";
  const stats = Array.isArray(b.stats) && b.stats.length
    ? b.stats.map((s) => ({ value: s.title || "", label: s.body || "" }))
    : [
        { value: "100%", label: "Custom-coded" },
        { value: "0",    label: "Templates" },
        { value: "PR",   label: "Reviewed every push" },
      ];
  return (
  <section
    data-testid="build-showcase"
    className="relative overflow-hidden bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="relative aspect-[16/9] md:aspect-[21/9] lg:aspect-[24/9] w-full">
      {/* Background video */}
      <video
        src={video}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={poster}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Editorial darken — keeps text readable, video atmospheric */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/92 via-[#0B0F14]/30 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/30 via-transparent to-[#0B0F14]/40"
      />

      {/* Overlay copy — left aligned editorial */}
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-3xl">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[3.25rem] lg:text-[4.25rem] font-black tracking-[-0.035em] leading-[1.0] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-300">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 max-w-[52ch] text-[1rem] md:text-[1.1rem] text-neutral-300 leading-[1.6]"
            >
              <span>{description}</span>
            </p>

            {/* Live stat row */}
            <div
              data-reveal="eyebrow"
              style={{ "--i": 3 }}
              className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400"
            >
              {stats.map((s, i) => (
                <span key={`${s.value}-${i}`} className="flex items-center gap-x-10">
                  <span className="flex items-center gap-3">
                    <span className="font-display text-[1.75rem] font-black text-[#F4D64E] tabular-nums leading-none">
                      {s.value}
                    </span>
                    <span>{s.label}</span>
                  </span>
                  {i < stats.length - 1 ? (
                    <span aria-hidden className="hidden md:inline w-px h-8 bg-white/10" />
                  ) : null}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subtle yellow blur in corner */}
      <span
        aria-hidden
        className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#F4D64E]/[0.06] blur-3xl pointer-events-none"
      />
    </div>
  </section>
  );
};

/* ============================================================
   05 · 250+ Possibilities — sticky-stacking buckets
============================================================ */
/* ----- Inline mini-demo widgets for each Possibility bucket ----- */

// 01 · Product Experience — animated swatch picker
const DemoSwatches = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % 5), 1800);
    return () => clearInterval(id);
  }, []);
  const colors = [
    { c: "#F4D64E", n: "Honey" },
    { c: "#EFEBE3", n: "Bone" },
    { c: "#1F2A36", n: "Slate" },
    { c: "#A85B3C", n: "Clay" },
    { c: "#5B6B5C", n: "Sage" },
  ];
  return (
    <div className="flex flex-col gap-7 w-full">
      <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#1F2A36] relative flex items-center justify-center">
        <motion.div
          key={active}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-32 h-44 md:w-40 md:h-56 rounded-lg shadow-2xl"
          style={{ background: colors[active].c }}
        />
        <span className="absolute top-4 left-4 text-[0.6rem] font-bold tracking-[0.22em] uppercase text-white/70">
          Product · Live preview
        </span>
        <span className="absolute bottom-4 left-4 text-[0.85rem] font-bold text-white">
          {colors[active].n}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {colors.map((c, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            className={`relative w-9 h-9 rounded-full border-2 transition-all ${
              active === i ? "border-[#F4D64E] scale-110" : "border-white/20 hover:border-white/50"
            }`}
            style={{ background: c.c }}
            aria-label={c.n}
          />
        ))}
      </div>
    </div>
  );
};

// 02 · Checkout & Cart — cart progress bar
const DemoCart = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 8));
    }, 500);
    return () => clearInterval(id);
  }, []);
  const target = 75;
  const remaining = Math.max(0, target - Math.round((progress / 100) * target));
  return (
    <div className="flex flex-col gap-6 w-full bg-[#0B0F14] rounded-xl p-6 md:p-7 border border-white/10">
      <div className="flex items-baseline justify-between">
        <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
          Cart
        </span>
        <span className="font-display text-[1.4rem] font-black text-white tabular-nums">
          ${Math.round((progress / 100) * target)}
        </span>
      </div>
      <div>
        <div className="text-[0.85rem] text-neutral-300 mb-3">
          {remaining > 0 ? (
            <>
              Spend <span className="text-[#F4D64E] font-bold">${remaining}</span> more for free shipping
            </>
          ) : (
            <span className="text-[#F4D64E] font-bold">🎉 Free shipping unlocked!</span>
          )}
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-[#F4D64E]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>
      <button
        type="button"
        className="w-full py-3 rounded-md bg-[#F4D64E] text-neutral-950 text-[0.78rem] font-bold tracking-[0.18em] uppercase"
      >
        Checkout
      </button>
    </div>
  );
};

// 03 · Inventory — stock counter / multi-warehouse dots
const DemoInventory = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1400);
    return () => clearInterval(id);
  }, []);
  const warehouses = [
    { n: "Brooklyn",  base: 12 },
    { n: "Los Angeles", base: 7 },
    { n: "London",    base: 24 },
  ];
  return (
    <div className="flex flex-col gap-3 w-full bg-[#0B0F14] rounded-xl p-6 md:p-7 border border-white/10">
      <div className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 mb-2">
        Live stock · Multi-warehouse
      </div>
      {warehouses.map((w, i) => {
        const stock = Math.max(0, w.base - ((tick + i * 2) % (w.base + 4)));
        const status = stock === 0 ? "out" : stock <= 3 ? "low" : "ok";
        return (
          <div key={w.n} className="flex items-center justify-between py-2.5 border-b border-white/[0.06] last:border-0">
            <div className="flex items-center gap-3">
              <motion.span
                animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className={`w-2 h-2 rounded-full ${
                  status === "ok" ? "bg-[#7CC069]" : status === "low" ? "bg-[#F4D64E]" : "bg-[#E5644E]"
                }`}
              />
              <span className="text-[0.92rem] text-white">{w.n}</span>
            </div>
            <span className="font-display text-[0.95rem] font-bold tabular-nums text-neutral-300">
              {stock === 0 ? "—" : `${stock} in stock`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// 04 · Loyalty — animated points counter
const DemoLoyalty = () => {
  const [pts, setPts] = useState(0);
  useEffect(() => {
    let frame;
    let v = 0;
    const tick = () => {
      v = (v + 7) % 1280;
      setPts(v);
      frame = requestAnimationFrame(tick);
    };
    const id = setTimeout(() => { frame = requestAnimationFrame(tick); }, 100);
    return () => { clearTimeout(id); cancelAnimationFrame(frame); };
  }, []);
  return (
    <div className="flex flex-col gap-5 w-full bg-[#0B0F14] rounded-xl p-6 md:p-7 border border-white/10">
      <div className="flex items-center gap-2 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
        <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
        Member rewards
      </div>
      <div className="flex items-baseline gap-3">
        <span className="font-display text-[3rem] md:text-[3.5rem] font-black tracking-[-0.04em] tabular-nums text-[#F4D64E] leading-none">
          {pts}
        </span>
        <span className="text-[0.78rem] font-bold tracking-[0.2em] uppercase text-neutral-400">
          points
        </span>
      </div>
      <div className="text-[0.85rem] text-neutral-300">
        <span className="text-[#F4D64E] font-bold">Gold tier</span> unlocked at 1,000 — free shipping for life.
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[#F4D64E] transition-[width] duration-300"
          style={{ width: `${Math.min(100, (pts / 1000) * 100)}%` }}
        />
      </div>
    </div>
  );
};

// 05 · Marketing — countdown banner / smart promo
const DemoMarketing = () => {
  const [t, setT] = useState({ h: 4, m: 32, s: 18 });
  useEffect(() => {
    const id = setInterval(() => {
      setT((p) => {
        let s = p.s - 1;
        let m = p.m;
        let h = p.h;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 5; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="rounded-xl bg-gradient-to-r from-[#F4D64E] to-[#FFE57A] p-5 md:p-6 text-neutral-950">
        <div className="text-[0.65rem] font-bold tracking-[0.22em] uppercase opacity-70 mb-2">
          Limited drop
        </div>
        <div className="font-display text-[1.1rem] md:text-[1.25rem] font-black leading-[1.1] mb-4">
          Spring Edit · 25% off ends in
        </div>
        <div className="flex items-center gap-3 font-display font-black tabular-nums">
          {[
            ["HRS", t.h],
            ["MIN", t.m],
            ["SEC", t.s],
          ].map(([l, v]) => (
            <div key={l} className="flex flex-col items-center bg-neutral-950/15 rounded-md px-3 py-2 min-w-[3.25rem]">
              <span className="text-[1.4rem] leading-none">{pad(v)}</span>
              <span className="text-[0.55rem] tracking-[0.2em] mt-1 opacity-70">{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-500 text-center">
        Auto-targeted to first-time visitors
      </div>
    </div>
  );
};

// 06 · UI/UX — sticky ATC bar mock
const DemoUIUX = () => {
  const [hover, setHover] = useState(false);
  return (
    <div className="relative w-full bg-[#0B0F14] rounded-xl border border-white/10 overflow-hidden">
      {/* Faux product image */}
      <div className="aspect-[4/3] relative">
        <img
          src="https://images.unsplash.com/photo-1560769629-975ec94e6a86?auto=format&fit=crop&w=1200&q=70"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B0F14]/95" />

        {/* Sticky ATC bar */}
        <motion.div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          animate={{ y: hover ? -4 : 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
          className="absolute bottom-3 left-3 right-3 flex items-center gap-3 bg-[#0F141A]/90 backdrop-blur-md rounded-lg border border-white/10 px-3 py-2.5"
        >
          <div className="w-10 h-10 rounded-md bg-white/5 overflow-hidden flex-shrink-0">
            <div className="w-full h-full bg-gradient-to-br from-[#F4D64E]/40 to-[#F4D64E]/10" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[0.78rem] font-bold text-white truncate">Linen Field Jacket</div>
            <div className="text-[0.7rem] text-neutral-400">Bone · M</div>
          </div>
          <button
            type="button"
            className="text-[0.7rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-3 py-2 rounded transition-colors"
          >
            Add — $148
          </button>
        </motion.div>
      </div>
      <div className="px-4 py-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 border-t border-white/[0.06]">
        Sticky ATC · always reachable
      </div>
    </div>
  );
};

const DEMO_BY_BUCKET = [
  DemoSwatches,
  DemoCart,
  DemoInventory,
  DemoLoyalty,
  DemoMarketing,
  DemoUIUX,
];

const BUCKETS = [
  {
    n: "01",
    eyebrow: "Product Experience & Merchandising",
    title: "Make every product feel handpicked.",
    items: [
      "Product bundle kits",
      "Custom product configurators",
      "Tiered pricing rules",
      "Mix & match bundles",
      "Advanced variant selectors",
      "Gift-with-purchase triggers",
      "Custom color swatches",
      "Dynamic size guides & fit charts",
    ],
  },
  {
    n: "02",
    eyebrow: "Checkout & Cart",
    title: "Recover revenue at the last mile.",
    items: [
      "Upsell & cross-sell at checkout",
      "Cart goal progress bars",
      "Custom discount logic",
      "Dynamic shipping thresholds",
      "Post-purchase upsell experiences",
    ],
  },
  {
    n: "03",
    eyebrow: "Inventory & Operations",
    title: "Operate at scale without chaos.",
    items: [
      "Multi-warehouse product visibility",
      "Custom backorder messaging",
      "Advanced pre-order systems",
      "Real-time stock automation",
      "Gift wrapping & personalization logic",
    ],
  },
  {
    n: "04",
    eyebrow: "User Engagement & Loyalty",
    title: "Turn first-time buyers into regulars.",
    items: [
      "Dynamic rewards integration",
      "Custom referral flows",
      "Interactive wishlists",
      "Advanced review display systems",
      "Dynamic announcement bars",
    ],
  },
  {
    n: "05",
    eyebrow: "Marketing & Conversion",
    title: "Ship campaigns without dev tickets.",
    items: [
      "Custom landing page builders",
      "Campaign-specific checkout flows",
      "Discount code restrictions",
      "Tracking & analytics integrations",
      "Smart banners & personalized content",
    ],
  },
  {
    n: "06",
    eyebrow: "UI/UX Enhancements",
    title: "Storefront craft, in motion.",
    items: [
      "Dynamic homepage & PDP sections",
      "Sticky ATC bars & floating elements",
      "Video-based product galleries",
      "Micro-interactions & animations",
      "Custom quick-view modules",
    ],
  },
];

const PossibilitiesIntro = () => {
  const cms = useContent("shopify-dev");
  const p = cms?.possibilities || {};
  const eyebrow = p.eyebrow || "Custom development · 250+ possibilities";
  const headlineMain = p.headline_main || "If you can";
  const headlineItalic = p.headline_italic || "imagine";
  const headlineAccent = p.headline_accent || "build it";
  const description = p.description ||
    "From storefront to checkout, we build custom functionality across every layer of Shopify and Shopify Plus — with one-time coded solutions, not recurring app costs.";
  return (
  <section className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="max-w-4xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.5rem] md:text-[4rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
        >
          <span>
            {headlineMain}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                it — we can{" "}
              </>
            ) : null}
            <PillarAccent>{headlineAccent}</PillarAccent>.
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-10 max-w-[60ch] text-[1.05rem] md:text-[1.15rem] text-neutral-400 leading-[1.65]"
        >
          <span>{description}</span>
        </p>
      </div>
    </div>
  </section>
  );
};

const PossibilityCard = ({ bucket, i, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const opacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.7, 0.4]);
  const Demo = DEMO_BY_BUCKET[i] || (() => null);

  return (
    <div
      ref={ref}
      // MOBILE: stacked, full-width, natural height, generous spacing.
      // DESKTOP (lg+): sticky-pinned, full-viewport stacking effect.
      className="relative bg-[#0B0F14] py-12 lg:py-0 lg:sticky lg:top-0 lg:h-screen lg:flex lg:items-center"
      style={{ zIndex: i + 1 }}
    >
      <motion.div
        // Scale/opacity scroll effect only meaningful on desktop sticky stacking.
        // On mobile we leave default rendering untouched (cards just stack).
        style={
          typeof window !== "undefined" && window.innerWidth >= 1024
            ? { scale, opacity }
            : undefined
        }
        className="w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10"
      >
        <div className="rounded-2xl lg:rounded-3xl border border-white/[0.06] bg-[#0F141A] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-0">
            {/* LEFT — copy + features list */}
            <div className="lg:col-span-7 p-6 sm:p-8 md:p-10 lg:p-14 flex flex-col justify-between gap-7 lg:gap-10">
              <div>
                <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-7 flex-wrap">
                  <span className="text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums">
                    {bucket.n} / {String(total).padStart(2, "0")}
                  </span>
                  <span className="hidden sm:inline h-px flex-1 bg-white/[0.06] max-w-[100px]" />
                  <span className="text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                    {bucket.eyebrow}
                  </span>
                </div>
                <h3 className="font-display text-[1.5rem] sm:text-[1.85rem] md:text-[2.25rem] lg:text-[2.85rem] font-black tracking-[-0.035em] leading-[1.05] text-white mb-6 sm:mb-7">
                  {bucket.title}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 sm:gap-y-2 mt-2">
                  {bucket.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-center gap-3 py-1.5 text-[0.88rem] sm:text-[0.92rem] text-neutral-300"
                    >
                      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]/70 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-baseline gap-3 text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                <span className="font-display text-[1.85rem] sm:text-[2.25rem] md:text-[2.5rem] lg:text-[2.75rem] font-black text-[#F4D64E] tabular-nums leading-none">
                  {String(bucket.items.length).padStart(2, "0")}
                </span>
                <span>features in this stack</span>
              </div>
            </div>

            {/* RIGHT — live mini-demo */}
            <div className="lg:col-span-5 p-6 sm:p-8 md:p-10 lg:p-14 lg:border-l border-t lg:border-t-0 border-white/[0.06] bg-[#0B0F14]/60 flex items-center justify-center">
              <Demo />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Possibilities = () => {
  const cms = useContent("shopify-dev");
  const liveBuckets = Array.isArray(cms?.possibilities?.buckets) && cms.possibilities.buckets.length
    ? cms.possibilities.buckets.map((b, i) => ({
        n: b.tag_number || String(i + 1).padStart(2, "0"),
        eyebrow: b.tag_label || "",
        title: b.title || "",
        items: Array.isArray(b.highlights) ? b.highlights : [],
      }))
    : BUCKETS;
  return (
  <section data-testid="possibilities" className="relative bg-[#0B0F14]">
    <PossibilitiesIntro />
    <div className="relative">
      {liveBuckets.map((b, i) => (
        <PossibilityCard key={i} bucket={b} i={i} total={liveBuckets.length} />
      ))}
    </div>
    {/* Spacer so we exit the sticky stack cleanly — desktop only */}
    <div className="hidden lg:block lg:h-[20vh]" />
  </section>
  );
};

/* ============================================================
   06 · Process — sticky-pinned vertical timeline
   - Left column pins as user scrolls through the section
   - Yellow vertical line draws downward with scroll progress
   - Active step swaps on the right with crossfade
============================================================ */
const PROCESS = [
  { n: "01", t: "Discovery", b: "We start with your business — goals, constraints, and the metric that matters most. Nothing else gets built until that's clear." },
  { n: "02", t: "Strategy", b: "A clear technical specification: scope, stack, milestones, and the decisions we're making before a single pixel moves." },
  { n: "03", t: "Design", b: "UI/UX that feels like your brand, not a Shopify template. Mobile-first, motion where it earns attention." },
  { n: "04", t: "Build", b: "Senior engineers writing custom Liquid, React, and Hydrogen — not assembling apps. Code reviews on every PR." },
  { n: "05", t: "QA", b: "Cross-browser, cross-device, real-data testing. Performance budgets enforced before launch, not after." },
  { n: "06", t: "Launch", b: "Coordinated cutover with zero data loss. We sit with your team on launch day until everything is stable." },
  { n: "07", t: "Support", b: "Flexible monthly retainers for ongoing optimization, A/B testing, and the next thing on your roadmap." },
];

const Process = () => {
  const cms = useContent("shopify-dev");
  const p = cms?.process || {};
  const eyebrow = p.eyebrow || "How we work";
  const headlineMain = p.headline_main || "Seven steps,";
  const headlineItalic = p.headline_italic || "no";
  const headlineAccent = p.headline_accent || "surprises";
  const description = p.description ||
    "Transparent and systematic — you always see where we are, what's next, and what we're deciding right now.";
  const liveSteps = Array.isArray(p.items) && p.items.length
    ? p.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        t: it.title || "",
        b: it.body || "",
      }))
    : PROCESS;
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Map scroll progress (0-1) → step index (0..N-1) — desktop only
  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (v) => {
      const idx = Math.min(
        liveSteps.length - 1,
        Math.max(0, Math.floor(v * liveSteps.length))
      );
      setActive(idx);
    });
    return () => unsubscribe();
  }, [scrollYProgress, liveSteps.length]);

  // Vertical line fill — scales 0 → 1 as user scrolls through section
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="process"
      data-testid="process"
      ref={sectionRef}
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
      {/* ============================================================
          MOBILE — full-width stacked editorial cards.
          Each step is a self-contained block with a giant numeral,
          title and body. No sticky/pinned behaviour, no horizontal
          truncation, no crossfade — just clean reading.
      ============================================================ */}
      <div className="lg:hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          {/* Section header */}
          <div className="mb-12 md:mb-16">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-6 font-display text-[1.85rem] sm:text-[2.25rem] md:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.05] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 text-[0.98rem] md:text-[1.075rem] text-neutral-400 leading-[1.6] max-w-[60ch]"
            >
              <span>{description}</span>
            </p>
          </div>

          {/* Steps stacked — chip + connector form one continuous left rail.
              Body text sits clearly to the right with breathing room so the
              rail visually links the numbers without crossing the copy. */}
          <ul className="flex flex-col gap-14 md:gap-16">
            {liveSteps.map((step, i) => {
              const isLast = i === liveSteps.length - 1;
              return (
                <li
                  key={i}
                  data-testid={`process-step-mobile-${step.n}`}
                  data-reveal="mask-block"
                  style={{ "--i": Math.min(i, 5) }}
                  className="grid grid-cols-[3.25rem_1fr] gap-x-7 sm:gap-x-9 items-start"
                >
                  {/* Left rail column — chip + connector to next chip */}
                  <div className="relative flex justify-center self-stretch">
                    <span
                      aria-hidden
                      className="relative z-10 inline-flex items-center justify-center rounded-full bg-[#F4D64E] text-neutral-950 font-display font-black text-[1rem] tabular-nums tracking-tight leading-none flex-shrink-0"
                      style={{ width: "3.25rem", height: "3.25rem" }}
                    >
                      {step.n}
                    </span>
                    {!isLast && (
                      <>
                        {/* Hairline connector — sits between chips */}
                        <span
                          aria-hidden
                          className="absolute top-[3.25rem] bottom-[-2.25rem] left-1/2 -translate-x-1/2 w-px bg-white/[0.18]"
                        />
                        {/* Yellow chevron — flow indicator pointing at next chip */}
                        <svg
                          aria-hidden
                          className="absolute bottom-[-2.75rem] left-1/2 -translate-x-1/2"
                          width="16"
                          height="9"
                          viewBox="0 0 16 9"
                          fill="none"
                        >
                          <path
                            d="M1 1 L8 7 L15 1"
                            stroke="#F4D64E"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity="0.9"
                          />
                        </svg>
                      </>
                    )}
                  </div>
                  {/* Right column — body */}
                  <div className="pt-2.5">
                    <h3 className="font-display text-[1.5rem] sm:text-[1.75rem] font-black tracking-[-0.025em] text-white leading-[1.1]">
                      {step.t}.
                    </h3>
                    <p className="mt-3 text-[0.95rem] sm:text-[1rem] text-neutral-400 leading-[1.6]">
                      {step.b}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* ============================================================
          DESKTOP — sticky-pinned vertical timeline (lg+ only)
      ============================================================ */}
      <div
        className="hidden lg:block"
        style={{ minHeight: `${liveSteps.length * 75}vh` }}
      >
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="max-w-7xl mx-auto px-10 w-full">
            {/* Section header */}
            <div className="grid grid-cols-12 gap-x-12 mb-14">
              <div className="col-span-5">
                <span data-reveal="eyebrow">
                  <Eyebrow>{eyebrow}</Eyebrow>
                </span>
                <h2
                  data-reveal="mask-block"
                  style={{ "--i": 1 }}
                  className="mt-6 font-display text-[2.5rem] xl:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
                >
                  <span>
                    {headlineMain}{" "}
                    {headlineItalic ? (
                      <>
                        <span className="italic font-normal text-neutral-400">
                          {headlineItalic}
                        </span>{" "}
                      </>
                    ) : null}
                    <PillarAccent>{headlineAccent}</PillarAccent>.
                  </span>
                </h2>
              </div>
              <div className="col-span-6 col-start-7 self-end">
                <p
                  data-reveal="mask-block"
                  style={{ "--i": 2 }}
                  className="text-[1.075rem] text-neutral-400 leading-[1.6]"
                >
                  <span>{description}</span>
                </p>
              </div>
            </div>

            {/* Two-column timeline */}
            <div className="grid grid-cols-12 gap-x-12 items-start">
              {/* LEFT — vertical list with drawing yellow line */}
              <div className="col-span-5 relative">
                <span
                  aria-hidden
                  className="absolute left-[19px] top-2 bottom-2 w-px bg-white/[0.08]"
                />
                <motion.span
                  aria-hidden
                  style={{ scaleY: lineScaleY }}
                  className="absolute left-[19px] top-2 bottom-2 w-px bg-[#F4D64E] origin-top"
                />

                <ul className="flex flex-col gap-1">
                  {liveSteps.map((step, i) => {
                    const isActive = i === active;
                    const isPassed = i < active;
                    return (
                      <li
                        key={i}
                        data-testid={`process-step-${step.n}`}
                        className="relative flex items-center gap-6 py-3"
                      >
                        <span
                          aria-hidden
                          className={`relative z-10 inline-flex items-center justify-center w-10 h-10 rounded-full border-2 text-[0.7rem] font-bold tracking-[0.18em] uppercase tabular-nums transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                            isActive
                              ? "bg-[#F4D64E] text-neutral-950 border-[#F4D64E] scale-110"
                              : isPassed
                                ? "bg-[#0B0F14] text-[#F4D64E] border-[#F4D64E]"
                                : "bg-[#0B0F14] text-neutral-500 border-white/15"
                          }`}
                        >
                          {step.n}
                        </span>
                        <span
                          className={`font-display text-[1.2rem] font-bold tracking-[-0.01em] transition-colors duration-500 ${
                            isActive
                              ? "text-white"
                              : isPassed
                                ? "text-neutral-300"
                                : "text-neutral-500"
                          }`}
                        >
                          {step.t}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* RIGHT — pinned active step display with crossfade */}
              <div className="col-span-7 col-start-7 relative min-h-[22rem]">
                {liveSteps.map((step, i) => {
                  const isActive = i === active;
                  return (
                    <motion.article
                      key={i}
                      initial={false}
                      animate={{
                        opacity: isActive ? 1 : 0,
                        y: isActive ? 0 : 20,
                        pointerEvents: isActive ? "auto" : "none",
                      }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0"
                    >
                      <div className="font-display text-[7rem] xl:text-[9rem] font-black tracking-[-0.045em] leading-none text-[#F4D64E] tabular-nums">
                        {step.n}
                      </div>
                      <h3 className="mt-6 font-display text-[2.75rem] xl:text-[3.25rem] font-black tracking-[-0.035em] leading-[1.02] text-white">
                        {step.t}.
                      </h3>
                      <p className="mt-8 max-w-[52ch] text-[1.2rem] text-neutral-300 leading-[1.6]">
                        {step.b}
                      </p>
                      <div className="mt-10 flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                        <span className="tabular-nums text-[#F4D64E]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="h-px w-12 bg-white/[0.1]" />
                        <span className="tabular-nums">
                          of {String(liveSteps.length).padStart(2, "0")}
                        </span>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   07 · Tech stack marquee
============================================================ */
const TECH_STACK = [
  "Shopify Plus",
  "Liquid",
  "Hydrogen",
  "Storefront API",
  "React",
  "GraphQL",
  "TypeScript",
  "Tailwind",
  "Klaviyo",
  "Algolia",
  "Stripe",
  "Recharge",
  "Loop Returns",
  "Gorgias",
  "Yotpo",
  "Contentful",
];

const TechStack = () => {
  const cms = useContent("shopify-dev");
  const t = cms?.tech_stack || {};
  const eyebrow = t.eyebrow || "Stack & integrations";
  const headlineMain = t.headline_main || "The tools we use,";
  const headlineItalic = t.headline_italic || "daily";
  const liveItems = Array.isArray(t.items) && t.items.length
    ? t.items.map((it) => it.title || "").filter(Boolean)
    : TECH_STACK;
  const loop = useMemo(() => [...liveItems, ...liveItems], [liveItems]);
  return (
    <section
      data-testid="tech-stack"
      className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10">
        <div className="max-w-3xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold tracking-[-0.035em] leading-[1.05] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
              ) : null}
              .
            </span>
          </h2>
        </div>
      </div>

      <div className="relative pt-6 pb-20 md:pb-28">
        <div
          className="flex items-center will-change-transform animate-[marquee-left_120s_linear_infinite]"
          style={{ width: "max-content" }}
        >
          {loop.map((t, i) => (
            <span
              key={i}
              className="flex items-center whitespace-nowrap leading-none"
            >
              <span
                className="font-display font-black tracking-[-0.03em] uppercase text-white"
                style={{ fontSize: "clamp(1.75rem, 4.5vw, 4.5rem)" }}
              >
                {t}
              </span>
              <span
                aria-hidden
                className="mx-8 md:mx-14 inline-block w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#F4D64E]"
              />
            </span>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 md:w-60 bg-gradient-to-r from-[#0B0F14] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 md:w-60 bg-gradient-to-l from-[#0B0F14] to-transparent" />
      </div>
    </section>
  );
};

/* ============================================================
   08 · Who we work with — typographic statement
============================================================ */
const WhoWeWorkWith = ({ onPrimary }) => {
  const cms = useContent("shopify-dev");
  const w = cms?.who_we_work_with || {};
  const eyebrow = w.eyebrow || "Who we partner with";
  const headlinePart1 = w.headline_part_1 || "Founders ready to scale.";
  const headlineItalic = w.headline_italic || "eCommerce";
  const headlinePart2 = w.headline_part_2 || "teams growing fast.";
  const headlineAccent = w.headline_accent || "Brands";
  const headlinePart3 = w.headline_part_3 || "elevating Shopify.";
  const description = w.description ||
    "We build and optimize — not validate. We collaborate with teams that have a clear vision and are ready to move efficiently toward growth with tailored, results-driven solutions.";
  const ctaLabel = w.cta_label || "Let's talk";
  return (
  <section
    data-testid="who-we-work-with"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="max-w-5xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2rem] md:text-[3rem] lg:text-[3.75rem] font-black tracking-[-0.035em] leading-[1.02] text-white"
        >
          <span>
            {headlinePart1}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
              </>
            ) : null}
            {headlinePart2 ? <>{headlinePart2}{" "}</> : null}
            <PillarAccent>{headlineAccent}</PillarAccent>{headlinePart3 ? <> {headlinePart3}</> : null}
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-10 max-w-[60ch] text-[1.05rem] md:text-[1.15rem] text-neutral-400 leading-[1.65]"
        >
          <span>{description}</span>
        </p>
        <div data-reveal="eyebrow" style={{ "--i": 3 }} className="mt-12">
          <PrimaryCTA testId="who-cta" onClick={onPrimary}>
            {ctaLabel}
          </PrimaryCTA>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function ShopifyDev() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/shopify-experts-development",
    fallback: {
      title: "Shopify Development — Sunday Digital",
      description: "Custom Shopify Plus development. Themes, apps, migrations, headless storefronts.",
    },
  });
  useEffect(() => {
    document.title = "Shopify Development — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer — same setup as HomeV5
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="shopify-dev-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <ShopifyDevHero onPrimary={openContact} />
        <TrustStripDev />
        <WhatYouGet />
        <Capabilities />
        <BuildShowcase />
        <Possibilities />
        <Process />
        <TechStack />
        <WhoWeWorkWith onPrimary={openContact} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}

/* ============================================================
   Re-exports for V2 page (and any future variant)
============================================================ */
export {
  TrustStripDev,
  WhatYouGet,
  Capabilities,
  BuildShowcase,
  Possibilities,
  Process,
  TechStack,
  WhoWeWorkWith,
};
